import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { ContentKitLifecycle, type ContentKitRemoteInput } from '@gitbook/contentkit-react';
import { webConfig } from '@gitbook/env';
import { useThemeVariants } from '@gitbook/react-uikit';
import { ActionableContainerSkeleton } from '@gitbook/spine-react';

import { useContentKitRemote } from '../../state';
import { contentKitRemoteComponents } from '../ContentKit';
import { IntegrationFormSchema } from './IntegrationFormSchema';

export interface IntegrationConfigureGenericFlowProps {
    /** Integration to configure */
    integration: api.Integration;
    /** Configuration object */
    configuration: api.IntegrationConfiguration;
    /** Props to be passed to contentkit */
    contentKitProps: any;
    /** Context to be passed to contentkit */
    contentKitContext:
        | Omit<api.ContentKitContextConfigurationAccount, 'theme'>
        | Omit<api.ContentKitContextConfigurationSite, 'theme'>
        | Omit<api.ContentKitContextConfigurationSpace, 'theme'>;
    /** Value passed to the form */
    formValue: any;
    /** Base URL passed to the form */
    formBaseUrl: string;
    /** Callback when the value in the form is changing */
    formOnChangeValue: (value: any) => Promise<void>;
    /** Callback when the configuration is finished */
    onComplete?: () => void;
}

/**
 * Component to render a generic configuration flow for an integration.
 * This component should not be used directly, use <IntegrationConfigureOrgFlowById /> or <IntegrationConfigureSpaceFlowById /> instead.
 */
export function IntegrationConfigureGenericFlow(
    props: IntegrationConfigureGenericFlowProps
): React.ReactElement {
    const { configuration, formOnChangeValue, formBaseUrl, formValue } = props;

    return 'properties' in configuration ? (
        <IntegrationFormSchema
            baseUrl={formBaseUrl}
            schema={{
                type: 'object',
                ...configuration,
            }}
            value={formValue || {}}
            onChangeValue={async (value) => {
                await formOnChangeValue(value);
            }}
        />
    ) : (
        <IntegrationConfigureGenericFlowWithComponent {...props} />
    );
}

function IntegrationConfigureGenericFlowWithComponent(
    props: IntegrationConfigureGenericFlowProps
): React.ReactElement {
    const { integration, configuration, contentKitProps, contentKitContext, onComplete } = props;
    const componentId = 'componentId' in configuration ? configuration.componentId : null;

    if (!componentId) {
        throw new Error(
            `Expected a component ID for installation configuration of integration ${integration.name}`
        );
    }

    const { color } = useThemeVariants();

    const integrationBlocksRemote = useContentKitRemote();

    const input = React.useMemo<ContentKitRemoteInput>(() => {
        return {
            integrationId: integration.name,
            componentId,
            props: contentKitProps,
            state: undefined,
            context: {
                ...contentKitContext,
                theme: color,
            },
        };
    }, [integration.name, componentId, contentKitProps, contentKitContext, color]);

    return (
        <React.Suspense fallback={<ActionableContainerSkeleton />}>
            <ContentKitLifecycle
                mode="configuration"
                remote={integrationBlocksRemote}
                remoteComponents={contentKitRemoteComponents}
                input={input}
                security={{
                    firstPartyDomains: [webConfig.integrations.domain],
                }}
                onComplete={() => {
                    onComplete?.();
                }}
            />
        </React.Suspense>
    );
}
